import { CHARACTER_MODERATION_STATUS, CHARACTER_VISIBILITY, ICharacter, useTranslatedTag, useTranslation } from "@next-platform/client-common-core";
import { CharacterCardActions, CharacterCardVariant } from "@next-platform/client-common-spicy-ui";
import { useMemo } from "react";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { getCharacterCardStatusBadgeLabel, getCharacterCardVariantLabel } from "@/utils";

import { CharacterCardData, DisplayContext } from "../../types";

interface IUseCharacterDataProps {
  charHits: ICharacter[];
  likedBots?: string[];
  chatLanguage?: string;
  dislikedRecommendedCharacterIds: string[];
  displayContext?: DisplayContext;
  isPartner: boolean;
  blurNSFW?: boolean;
  isAuthenticated: boolean;
}

export const useCharacterData = ({
  charHits,
  likedBots,
  chatLanguage,
  dislikedRecommendedCharacterIds,
  displayContext,
  isPartner,
  blurNSFW,
  isAuthenticated,
}: IUseCharacterDataProps): CharacterCardData[] => {
  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);

  return useMemo(() => {
    return (charHits || []).map((character) => {
      const characterId = character?.character_id || character?.id;
      const isCharacterLiked = !!likedBots?.includes(character?.character_id || character?.id);
      const isHidden = dislikedRecommendedCharacterIds.includes(characterId) && !!character?.recommId;
      const tags = character?.tags?.map((tag) => getTranslatedTag(tag));

      let characterCardVariant = CharacterCardVariant.DEFAULT;
      const moderationStatus = character?.moderationReport?.moderation_status;
      const visibility = character?.visibility;

      if (displayContext === DisplayContext.MEMBER_SELECT) {
        if (visibility === CHARACTER_VISIBILITY.PUBLIC) {
          characterCardVariant = CharacterCardVariant.PUBLIC;
        } else if (visibility === CHARACTER_VISIBILITY.PRIVATE || visibility === CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE) {
          characterCardVariant = CharacterCardVariant.PRIVATE;
        } else if (visibility === CHARACTER_VISIBILITY.HIDDEN) {
          characterCardVariant = CharacterCardVariant.UNLISTED;
        }
      } else if (displayContext === DisplayContext.MY_CHATBOTS) {
        // Priority: moderation status > visibility status
        if (moderationStatus === CHARACTER_MODERATION_STATUS.PENDING || moderationStatus === CHARACTER_MODERATION_STATUS.REREVIEW_REQUIRED) {
          characterCardVariant = CharacterCardVariant.REVIEW;
        } else if (moderationStatus === CHARACTER_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED) {
          characterCardVariant = CharacterCardVariant.UNDER_MANUAL_REVIEW;
        } else if (moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED) {
          characterCardVariant = CharacterCardVariant.ACTION;
        } else if (visibility === CHARACTER_VISIBILITY.PUBLIC) {
          characterCardVariant = CharacterCardVariant.PUBLIC;
        } else if (visibility === CHARACTER_VISIBILITY.PRIVATE || visibility === CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE) {
          characterCardVariant = CharacterCardVariant.PRIVATE;
        } else if (visibility === CHARACTER_VISIBILITY.HIDDEN) {
          characterCardVariant = CharacterCardVariant.UNLISTED;
        }
      } else {
        characterCardVariant = character?.recommId ? CharacterCardVariant.RECOMMENDED : CharacterCardVariant.DEFAULT;
      }
      const variantLabel = getCharacterCardVariantLabel(characterCardVariant, t);
      const statusBadgeLabel = getCharacterCardStatusBadgeLabel(characterCardVariant, t);
      const visibilityBadgeByVisibility = {
        [CHARACTER_VISIBILITY.PUBLIC]: t("myCreation:card.statusBadge.public"),
        [CHARACTER_VISIBILITY.PRIVATE]: t("myCreation:card.statusBadge.private"),
        [CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE]: t("myCreation:card.statusBadge.private"),
        [CHARACTER_VISIBILITY.HIDDEN]: t("myCreation:card.statusBadge.unlisted"),
      } as const;
      const resolvedVisibility = character?.visibility
        ? visibilityBadgeByVisibility[character.visibility as keyof typeof visibilityBadgeByVisibility]
        : undefined;
      const visibilityBadgeLabel = resolvedVisibility ?? undefined;

      return {
        isHidden,
        characterAvatarBlur: !!blurNSFW && character?.avatar_is_nsfw,
        characterId,
        characterName: character?.name_i18n?.[chatLanguage ?? ""] || character?.name,
        characterVisibility: character?.visibility,
        visibilityBadgeLabel,
        characterDefinitionVisible: character?.definition_visible,
        characterCreatorUserName: character?.creator_username,
        characterTitle: character?.title_i18n?.[chatLanguage ?? ""] || character?.title,
        characterTags: tags,
        characterMessageNumber: character?.num_messages ?? 0,
        characterRatingScore: character?.rating_score ?? 0,
        characterTokensCount: character?.token_count ?? 0,
        characterLikeLabel: isCharacterLiked ? t("chatbotCard:tooltip.unlike") : t("chatbotCard:tooltip.like"),
        characterInfoLabel: character?.definition_visible ? t("chatbotCard:tooltip.definitionVisible") : t("chatbotCard:tooltip.definitionHidden"),
        characterAvatarUrl: (character?.avatar_url as string) ?? "",
        characterImageGen: character?.lora_status === "ready" && !isPartner,
        characterImageGenLabel: t("chatbotCard:tooltip.imageGen"),
        hasVoice: hasVoiceAttachmentFeatureFlag && !!character?.voice_id,
        characterCardVariant,
        variantLabel,
        statusBadgeLabel,
        creatorPagePath: `/creator/${character?.creator_username}`,
        chatPagePath: `/chat/${characterId}`,
        chatbotProfilePagePath: `/chatbot/${characterId}`,
        characterActionsState: {
          [CharacterCardActions.FAVORITE]: {
            isDisabled: false,
            isHidden: !isAuthenticated || displayContext === DisplayContext.MY_CHATBOTS,
            isLiked: isCharacterLiked,
          },
        },
      };
    });
  }, [
    charHits,
    likedBots,
    chatLanguage,
    dislikedRecommendedCharacterIds,
    displayContext,
    blurNSFW,
    isPartner,
    isAuthenticated,
    hasVoiceAttachmentFeatureFlag,
    getTranslatedTag,
    t,
  ]);
};
